import React, { useState, useRef } from 'react'
import { motion, AnimatePresence, Reorder } from 'framer-motion'
import {
  formatFileSize,
  validateVideoFiles
} from '../../utils/videoUtils'

interface FormMultiVideoUploadProps {
  label: string
  name: string
  value: File[]
  onChange: (files: File[]) => void
  error?: string
  touched?: boolean
  required?: boolean
  disabled?: boolean
  maxSize?: number // in MB (default 500MB per video)
  maxVideos?: number // maximum number of videos allowed (default 5)
  helpText?: string
  uploadProgress?: { [key: string]: number }
  isUploading?: boolean
}

export const FormMultiVideoUpload: React.FC<FormMultiVideoUploadProps> = ({
  label,
  name,
  value = [],
  onChange,
  error,
  touched,
  required = false,
  disabled = false,
  maxSize = 500, // 500MB per video
  maxVideos = 5,
  helpText,
  uploadProgress = {},
  isUploading = false
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasError = touched && error

  const handleFileSelect = (newFiles: FileList | null) => {
    if (!newFiles || newFiles.length === 0) return

    const filesToAdd = Array.from(newFiles)
    const allFiles = [...value, ...filesToAdd]

    // Validate the files
    const validation = validateVideoFiles(allFiles, maxVideos, maxSize)
    if (!validation.valid) {
      setValidationError(validation.errors[0])
      return
    }

    setValidationError(null)
    onChange(allFiles)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    handleFileSelect(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const removeFile = (index: number) => {
    const updatedFiles = value.filter((_, i) => i !== index)
    onChange(updatedFiles)
  }

  const handleReorder = (newOrder: File[]) => {
    onChange(newOrder)
  }

  return (
    <div className="w-full">
      <label className={`block text-sm font-medium mb-1 transition-colors duration-200 ${
        hasError ? 'text-red-600' : 'text-gray-700'
      }`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        <span className="text-gray-500 font-normal ml-1">
          ({value.length}/{maxVideos})
        </span>
      </label>

      {/* Upload area */}
      {value.length < maxVideos && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 mb-4
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
            ${isDragging ? 'border-blue-500 bg-blue-50' : ''}
            ${hasError || validationError ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
          `}
        >
          <input
            ref={fileInputRef}
            id={name}
            name={name}
            type="file"
            multiple
            accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogg,.mov"
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={disabled || value.length >= maxVideos}
            className="sr-only"
            aria-invalid={hasError || !!validationError ? 'true' : 'false'}
          />

          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm-4 27V17l11 7-11 7z"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || value.length >= maxVideos}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400"
            >
              {value.length === 0 ? 'Click to upload videos' : 'Add more videos'}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              or drag and drop
            </p>
            <p className="mt-2 text-xs text-gray-400">
              MP4, WebM, OGG or MOV up to {maxSize}MB each
            </p>
          </div>
        </div>
      )}

      {/* Selected files list with reordering */}
      <AnimatePresence>
        {value.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2 mb-4"
          >
            <p className="text-xs font-medium text-gray-600">
              {value.length} video{value.length !== 1 ? 's' : ''} selected
              {value.length > 1 && <span className="text-gray-400 ml-1">(drag to reorder)</span>}
            </p>
            <Reorder.Group
              axis="y"
              values={value}
              onReorder={handleReorder}
              className="space-y-2"
            >
              {value.map((file, index) => (
                <Reorder.Item
                  key={file.name + index}
                  value={file}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-move"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M16 4v12a4 4 0 11-8 0V5h.5a1.5 1.5 0 110 3H8V5h4a2 2 0 110 4H8a4 4 0 11-8 0v12a4 4 0 11-8 0V5h.5a1.5 1.5 0 110 3H0V5h8V4h8z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    {isUploading && uploadProgress[file.name] !== undefined && (
                      <div className="ml-2 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-blue-600">
                            {uploadProgress[file.name]}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {!isUploading && (
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="ml-2 flex-shrink-0 text-red-600 hover:text-red-500 transition-colors"
                      title="Remove video"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error messages */}
      <AnimatePresence>
        {(hasError || validationError) && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-sm text-red-600 mt-1"
          >
            {error || validationError}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Help text */}
      {helpText && !error && !validationError && (
        <p className="mt-1 text-xs text-gray-500">
          {helpText}
        </p>
      )}
    </div>
  )
}

export default FormMultiVideoUpload
